import React from 'react';
import { ArrowRight, Check, X, Zap } from 'lucide-react';
import { SiteSection, SiteSectionHeader } from '../../components/site';
import type { SolutionTransform } from './types';

const ManufacturingTransformLayout: React.FC<{ transform: SolutionTransform }> = ({ transform }) => (
  <SiteSection className="solution-transform-section solution-transform-section--premium solution-transform-section--manufacturing">
    <SiteSectionHeader
      eyebrow={transform.eyebrow}
      title={transform.title}
      subtitle={transform.subtitle}
      align="center"
    />

    <div className="mf-transform">
      <div className="mf-transform__showcase">
        <article className="mf-transform__panel mf-transform__panel--before">
          <header className="mf-transform__panel-head">
            <span className="mf-transform__label mf-transform__label--before">Before</span>
            <h3 className="mf-transform__panel-title">{transform.before.title}</h3>
          </header>
          <ul className="mf-transform__list">
            {transform.before.items.map((item) => (
              <li key={item}>
                <span className="mf-transform__icon mf-transform__icon--before" aria-hidden>
                  <X size={15} strokeWidth={2.5} />
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </article>

        <div className="mf-transform__spine" aria-hidden>
          <div className="mf-transform__spine-line" />
          <div className="mf-transform__spine-node">
            <span className="mf-transform__spine-icon">
              <Zap size={18} strokeWidth={2.25} />
            </span>
            <span className="mf-transform__spine-duration">{transform.arrowLabel}</span>
            <span className="mf-transform__spine-caption">to smart SKU</span>
          </div>
          <div className="mf-transform__spine-line mf-transform__spine-line--bottom" />
        </div>

        <article className="mf-transform__panel mf-transform__panel--after">
          <header className="mf-transform__panel-head">
            <span className="mf-transform__label mf-transform__label--after">After</span>
            <h3 className="mf-transform__panel-title">{transform.after.title}</h3>
          </header>
          <ul className="mf-transform__list">
            {transform.after.items.map((item) => (
              <li key={item}>
                <span className="mf-transform__icon mf-transform__icon--after" aria-hidden>
                  <Check size={15} strokeWidth={2.5} />
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </article>
      </div>

      <div className="mf-transform__footer" aria-hidden>
        <span className="mf-transform__footer-item mf-transform__footer-item--muted">Legacy SKU</span>
        <span className="mf-transform__footer-arrow">
          <ArrowRight size={16} strokeWidth={2.25} />
        </span>
        <span className="mf-transform__footer-item mf-transform__footer-item--accent">Connected product</span>
      </div>
    </div>
  </SiteSection>
);

const DefaultTransformLayout: React.FC<{ transform: SolutionTransform }> = ({ transform }) => (
  <SiteSection className="solution-transform-section solution-transform-section--premium">
    <SiteSectionHeader
      eyebrow={transform.eyebrow}
      title={transform.title}
      subtitle={transform.subtitle}
      align="center"
    />

    <div className="solution-transform">
      <article className="solution-transform__card solution-transform__card--before">
        <span className="solution-transform__badge solution-transform__badge--before">Before</span>
        <h3>{transform.before.title}</h3>
        <ul>
          {transform.before.items.map((item) => (
            <li key={item}>
              <span className="solution-transform__icon solution-transform__icon--before" aria-hidden>
                <X size={14} strokeWidth={2.5} />
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </article>

      <div className="solution-transform__bridge" aria-hidden>
        <div className="solution-transform__bridge-ring">
          <ArrowRight size={22} strokeWidth={2.25} />
        </div>
        <span className="solution-transform__bridge-label">{transform.arrowLabel}</span>
      </div>

      <article className="solution-transform__card solution-transform__card--after">
        <span className="solution-transform__badge solution-transform__badge--after">After</span>
        <h3>{transform.after.title}</h3>
        <ul>
          {transform.after.items.map((item) => (
            <li key={item}>
              <span className="solution-transform__icon solution-transform__icon--after" aria-hidden>
                <Check size={14} strokeWidth={2.5} />
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </article>
    </div>
  </SiteSection>
);

export const SolutionTransformSection: React.FC<{
  transform: SolutionTransform;
  variant?: 'default' | 'manufacturing';
}> = ({ transform, variant = 'default' }) => {
  if (variant === 'manufacturing') {
    return <ManufacturingTransformLayout transform={transform} />;
  }
  return <DefaultTransformLayout transform={transform} />;
};
